import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Package,
  Truck,
  MapPin,
  CheckCircle2,
  PackageCheck,
  AlertTriangle,
  Loader2,
  ArrowRight,
} from "lucide-react";
import Chevron from "@/components/Chevron";
import { useT } from "@/i18n/LanguageProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEP_ICONS = [Package, PackageCheck, Truck, MapPin, CheckCircle2];

/**
 * Mock tracking lookup — deterministic from the tracking number so the demo
 * looks alive. Anything starting with TWA is "found" with a status derived
 * from the last character (so users can try different IDs and see states).
 *
 * Replace with a real backend call when the tracking API exists.
 */
function mockLookup(tracking) {
  const code = (tracking || "").trim().toUpperCase();
  if (!code) return null;
  if (!code.startsWith("TWA")) return { error: true };

  const last = code.charCodeAt(code.length - 1);
  const stage = last % 5; // 0..4

  const now = new Date();
  const fmt = (d) =>
    d.toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
    });

  const placed = new Date(now.getTime() - 26 * 3600 * 1000);
  const picked = new Date(now.getTime() - 18 * 3600 * 1000);
  const inTransit = new Date(now.getTime() - 8 * 3600 * 1000);
  const outForDelivery = new Date(now.getTime() - 2 * 3600 * 1000);
  const delivered = new Date(now.getTime() - 0.5 * 3600 * 1000);
  const eta = new Date(now.getTime() + 1.5 * 3600 * 1000);

  const events = [
    { label: "Order Placed",     time: fmt(placed),         place: "Booked online · Muscat" },
    { label: "Picked Up",        time: fmt(picked),         place: "Muttrah · Muscat" },
    { label: "In Transit",       time: fmt(inTransit),      place: "Twasal Awasal sortation hub" },
    { label: "Out for Delivery", time: fmt(outForDelivery), place: "Rider · Qurum" },
    { label: "Delivered",        time: fmt(delivered),      place: "Recipient — signature captured" },
  ];

  return {
    tracking: code,
    currentStep: stage,
    eta: stage < 4 ? fmt(eta) : fmt(delivered),
    from: "Muttrah, Muscat",
    to: "Qurum Beach, Muscat",
    events,
  };
}

export default function TrackShipment() {
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tracking, setTracking] = useState(searchParams.get("id") || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Auto-run lookup if id is in the URL (e.g. coming from hero widget)
  useEffect(() => {
    const initial = searchParams.get("id");
    if (initial) {
      runLookup(initial);
    }
  }, [searchParams]);

  const runLookup = (value) => {
    setLoading(true);
    setSubmitted(true);
    // 600ms artificial delay so the spinner is visible & feels real
    setTimeout(() => {
      setResult(mockLookup(value));
      setLoading(false);
    }, 600);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!tracking.trim()) return;
    navigate(`/track?id=${encodeURIComponent(tracking.trim())}`, { replace: true });
    runLookup(tracking);
  };

  return (
    <>
      {/* Hero search section */}
      <section
        className="relative bg-delfi-dark text-white pt-36 pb-20 overflow-hidden"
        data-testid="track-hero"
      >
        <div className="chevron-bg right-[-10%] top-[-20%] opacity-[0.08]" aria-hidden="true">›</div>
        <div className="absolute inset-0 grid-pattern opacity-30" aria-hidden="true" />
        <div className="hero-glow left-[-200px] top-[-180px]" aria-hidden="true" />
        <div className="hero-glow right-[-260px] bottom-[-260px] opacity-70" aria-hidden="true" />

        <div className="delfi-container relative max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold mx-auto">
            <Chevron size={14} /> {t.track.eyebrow}
          </div>
          <h1
            className="headline-display text-white mt-5 text-[clamp(2.2rem,5.2vw,3.6rem)]"
            data-testid="track-page-title"
          >
            {t.track.title}
          </h1>
          <p className="mt-5 text-white/75 text-lg leading-relaxed max-w-2xl mx-auto">
            {t.track.subtitle}
          </p>

          <form
            onSubmit={handleSubmit}
            className="mt-9 flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto"
            data-testid="track-form"
          >
            <label className="sr-only" htmlFor="tracking-input">
              {t.track.placeholder}
            </label>
            <div className="relative flex-1">
              <Search
                size={18}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
              />
              <input
                id="tracking-input"
                type="text"
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder={t.track.placeholder}
                data-testid="track-input"
                className="w-full pl-11 pr-4 py-4 rounded-2xl bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-delfi-red focus:bg-white/[0.1] transition-colors text-base tracking-wide"
              />
            </div>
            <button
              type="submit"
              data-testid="track-submit"
              className="btn-delfi-primary justify-center"
              disabled={loading || !tracking.trim()}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> ...
                </>
              ) : (
                <>
                  {t.track.button} <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
          <p className="mt-4 text-xs text-white/40 tracking-wide">
            Example: <span className="text-white/65 font-mono">TWA123456789OM</span>
          </p>
        </div>
      </section>

      {/* Result section */}
      <section className="bg-delfi-cloud py-20 min-h-[40vh]" data-testid="track-result-section">
        <div className="delfi-container max-w-4xl">
          {!submitted && (
            <div className="rounded-3xl bg-white shadow-[0_30px_80px_-30px_rgba(46,46,46,0.18)] border border-delfi-navy/5 p-10 text-center">
              <Package size={32} className="text-delfi-red mx-auto" />
              <h2 className="text-2xl font-bold text-delfi-navy mt-4">
                Enter a tracking number above
              </h2>
              <p className="mt-2 text-delfi-navy/65 max-w-xl mx-auto">
                Once you submit your tracking number, you&apos;ll see a real-time status timeline from
                pickup to delivery.
              </p>
            </div>
          )}

          {submitted && loading && (
            <div className="rounded-3xl bg-white shadow-[0_30px_80px_-30px_rgba(46,46,46,0.18)] border border-delfi-navy/5 p-12 text-center">
              <Loader2 size={36} className="text-delfi-red mx-auto animate-spin" />
              <p className="mt-5 text-delfi-navy/70">Looking up your shipment…</p>
            </div>
          )}

          {submitted && !loading && result?.error && (
            <div
              className="rounded-3xl bg-white shadow-[0_30px_80px_-30px_rgba(46,46,46,0.18)] border border-delfi-navy/5 p-10"
              data-testid="track-not-found"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-delfi-red/10 text-delfi-red flex items-center justify-center shrink-0">
                  <AlertTriangle size={22} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-delfi-navy">{t.track.supportTitle}</h2>
                  <p className="mt-3 text-delfi-navy/70 leading-relaxed">
                    {t.track.notFoundMessage}
                  </p>
                  <Link
                    to="/partner"
                    className="btn-delfi-primary mt-6 inline-flex"
                    data-testid="track-contact-support"
                  >
                    {t.track.supportCta}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {submitted && !loading && result && !result.error && (
            <div
              className="rounded-3xl bg-white shadow-[0_30px_80px_-30px_rgba(46,46,46,0.18)] border border-delfi-navy/5 p-8 md:p-10"
              data-testid="track-result-card"
            >
              {/* Header strip */}
              <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-delfi-navy/10">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-delfi-red font-bold">
                    {t.track.yourShipment}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-delfi-navy font-mono">
                    {result.tracking}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-delfi-navy/50 font-semibold">
                      {t.track.fromTo}
                    </p>
                    <p className="text-delfi-navy/85 mt-0.5">
                      {result.from} → {result.to}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-delfi-navy/50 font-semibold">
                      {t.track.estimatedDelivery}
                    </p>
                    <p className="text-delfi-navy/85 mt-0.5">{result.eta}</p>
                  </div>
                </div>
              </div>

              {/* Status timeline */}
              <ol className="mt-8 space-y-6" data-testid="track-timeline">
                {(t.track.statusSteps || []).map((label, i) => {
                  const Icon = STEP_ICONS[i] || Package;
                  const isDone = i <= result.currentStep;
                  const isCurrent = i === result.currentStep;
                  return (
                    <li
                      key={label}
                      className="flex items-start gap-4 relative"
                      data-testid={`track-step-${i}`}
                    >
                      {/* connector */}
                      {i < (t.track.statusSteps || []).length - 1 && (
                        <span
                          className={`absolute left-5 top-10 w-0.5 h-12 ${
                            i < result.currentStep ? "bg-delfi-red" : "bg-delfi-navy/10"
                          }`}
                          aria-hidden="true"
                        />
                      )}
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${
                          isDone
                            ? "bg-delfi-red text-white shadow-[0_8px_22px_-8px_rgba(152,27,55,0.6)]"
                            : "bg-delfi-cloud text-delfi-navy/35 border border-delfi-navy/10"
                        } ${isCurrent ? "ring-4 ring-delfi-red/15" : ""}`}
                      >
                        <Icon size={18} strokeWidth={isDone ? 2.4 : 2} />
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <p
                            className={`font-semibold ${
                              isDone ? "text-delfi-navy" : "text-delfi-navy/45"
                            }`}
                          >
                            {label}
                          </p>
                          {isCurrent && (
                            <span className="text-[10px] uppercase tracking-wider text-delfi-red font-bold animate-pulse">
                              Live
                            </span>
                          )}
                        </div>
                        {isDone && (
                          <p className="text-sm text-delfi-navy/55 mt-1">
                            {result.events[i].time} · {result.events[i].place}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {/* Support prompt — always visible below */}
          <div
            className="mt-12 rounded-2xl bg-white border border-delfi-navy/10 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
            data-testid="track-support-prompt"
          >
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-delfi-red">
                {t.track.supportTitle}
              </p>
              <p className="mt-2 text-delfi-navy/70">{t.track.supportBody}</p>
            </div>
            <Link to="/partner" className="btn-delfi-ghost-dark">
              {t.track.supportCta}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
